import React, { Children, cloneElement, Component, PropTypes, PureComponent } from 'react';
import PageTitle from '../../../PageTitle';
import EmptyState from '../../../common/components/EmptyState';
import { NOTIFICATION_CONTEXT_TYPES } from '../../../NotificationSystemProvider';
import join from 'url-join';
import { Button, LoadingWrapper } from 'react-fast-ui';
import PillNav from '../../../components/PillNav';
import { TourneyBackLink } from '../../../FastRecruit/components/SessionsPage';
import moment from 'moment';
import { dateFormats } from '../../../util/constants';
import { Absolute, Relative } from '../../../common/components/Fill';
import TourneyModal from '../../../common/components/modals/TourneyModal';
import CityProvincePostal from '../../../components/CityProvincePostal';
import FormControl from '../../../components/inputs/FormControl';
import SuperAdminFeature from '../../../common/access/SuperAdminFeature';

const DEFAULT_OPTS = [
  { label: 'Structure', to: 'structure' },
  { label: 'Games', to: 'games' },
  { label: 'Brackets', to: 'brackets' }
];

class TourneyPills extends Component {
  render() {
    const { tourney } = this.props;

    return (
      <PillNav options={
        _.map(
          DEFAULT_OPTS,
          ({ label, to }) => ({ label, to: join('tourneys', tourney.id, to) })
        )
      }/>
    );
  }
}

const AdminTourneyFormFields = ({ value, onChange }) => (
  <div className="mg-top-sm display-flex">
    <div className="flex-grow-1 flex-basis-70">
      <label>Status</label>
      <FormControl>
        <select value={value ? value.status : ''} onChange={e => onChange({ ...value, status: e.target.value })}>
          <option value="" disabled hidden>Select...</option>
          <option value="Committed">Committed</option>
          <option value="Available">Available</option>
          <option value="Teams">Teams</option>
          <option value="Scheduled">Scheduled</option>
          <option value="Archived">Archived</option>
        </select>
      </FormControl>
    </div>

    <div className="flex-grow-1 flex-basis-30 mg-left-sm">
      <label>Source Type</label>
      <FormControl>
        <select
          value={value ? value.sourceType : ''}
          onChange={({ target: { value: sourceType } }) => onChange({
            ...value,
            sourceType: sourceType === '' ? null : sourceType
          })}>
          <option value="">N/A</option>
          <option value="FILE">File</option>
          <option value="SN">SN</option>
          <option value="EE">Exposure Events</option>
          <option value="TT">TT</option>
          <option value="TM">Tourney Machine</option>
        </select>
      </FormControl>
    </div>
  </div>
);

export default class AdminTourneyPage extends PureComponent {
  static contextTypes = {
    dao: PropTypes.object.isRequired,
    ...NOTIFICATION_CONTEXT_TYPES
  };

  static propTypes = {
    params: PropTypes.shape({
      tourneyId: PropTypes.string.isRequired
    }).isRequired
  };

  static defaultProps = {};

  state = {
    tourney: null,
    editing: null,
    promise: null
  };

  componentDidMount() {
    this.loadTourney(this.props.params.tourneyId);
  }

  componentWillReceiveProps({ params: { tourneyId } }) {
    if (this.props.params.tourneyId !== tourneyId) {
      this.setState({ tourney: null }, () => this.loadTourney(tourneyId));
    }
  }

  loadTourney = tourneyId => {
    const { promise } = this.state;
    if (!!promise) {
      promise.cancel();
    }

    this.setState({
      tourney: null,
      promise: this.context.dao.tourneys.getSingle(tourneyId)
        .then(
          tourney => this.setState({ tourney })
        )
        .catch(this.context.onError)
        .finally(
          () => this.setState({ promise: null })
        )
    });
  };

  handleEdit = editing => this.setState({ editing });
  openEdit = e => {
    e.preventDefault();
    this.handleEdit(this.state.tourney);
  };
  cancelEdit = () => this.handleEdit(null);
  handleSave = () => {
    this.setState({
      editing: null,
      promise: this.context.dao.tourneys.save(this.state.editing)
        .then(
          tourney => this.setState({ tourney })
        )
        .catch(this.context.onError)
        .finally(
          () => this.setState({ promise: null })
        )
    });
  };

  render() {
    const { tourney, promise, editing } = this.state;
    const { children } = this.props;

    if (tourney === null) {
      if (!!promise) {
        return EmptyState.LOADING;
      } else {
        return <EmptyState iconProps={{ name: 'trophy' }}>Failed to load tourney</EmptyState>;
      }
    }

    const { name, start, end, status, location, link } = tourney;
    const ft = t => moment(t, dateFormats.ISO_8601).format('L');

    return (
      <PageTitle title={name}>
        <Absolute className="display-flex flex-direction-column">
          <div>
            {
              editing &&
              <TourneyModal
                tourney={editing}
                onChange={this.handleEdit}
                onSave={this.handleSave}
                open={!!editing}
                additionalFields={AdminTourneyFormFields}
                onClose={this.cancelEdit}
                title="Edit Tourney"
              />
            }
          </div>

          <div className="flex-shrink-0">
            <LoadingWrapper loading={!!promise}>
              <div className="max-width">
                <TourneyBackLink tourney={tourney}/>
                <div className="display-flex align-items-center justify-content-center flex-wrap-wrap">
                  <div className="flex-grow-1">
                    <h2 style={{ marginBottom: 0 }}>
                      <span>{name}</span>
                      <small className="mg-left-sm"><em>{status}</em></small>
                    </h2>
                    {
                      link &&
                      <a href={link} target="_blank">{link}</a>
                    }
                    <h4>
                      {
                        location && location.address ?
                          <CityProvincePostal city={location.address.city} province={location.address.province}/> :
                          null
                      }
                    </h4>
                    <h4 className="text-muted">{ft(start)} - {ft(end)}</h4>
                  </div>
                  <SuperAdminFeature>
                    <div>
                      <Button onClick={this.openEdit} icon="pencil" text="Edit"/>
                    </div>
                  </SuperAdminFeature>
                </div>
              </div>
              <hr />
              <div className="max-width">
                <TourneyPills tourney={tourney}/>
              </div>
              <hr className="mg-bottom-xs"/>
            </LoadingWrapper>
          </div>
          <Relative className="flex-grow-1" style={{ minHeight: 400 }}>
            { Children.map(children, child => cloneElement(child, { tourney })) }
          </Relative>
        </Absolute>
      </PageTitle>
    );
  }
}


// WEBPACK FOOTER //
// ./src/js/GameDay/components/pages/AdminTourneyPage.jsx